/**
 * Dialog đổi mật khẩu user đang đăng nhập.
 * POST /api/auth/change-password với {old_password, new_password}.
 */
import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setOldPassword(""); setNewPassword(""); setConfirmPassword("");
    setShowOld(false); setShowNew(false);
  };

  const handleSave = async () => {
    if (!oldPassword) { toast.error("Nhập mật khẩu cũ"); return; }
    if (!newPassword || newPassword.length < 6) { toast.error("Mật khẩu mới phải ≥ 6 ký tự"); return; }
    if (newPassword !== confirmPassword) { toast.error("Mật khẩu xác nhận không khớp"); return; }
    if (newPassword === oldPassword) { toast.error("Mật khẩu mới phải khác mật khẩu cũ"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPassword, new_password: newPassword });
      toast.success("Đã đổi mật khẩu thành công. Lần sau đăng nhập bằng mật khẩu mới nhé.");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi đổi mật khẩu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-amber-400" /> Đổi mật khẩu
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Mật khẩu hiện tại</Label>
            <div className="relative">
              <Input
                type={showOld ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200 pr-9"
                data-testid="change-pw-old-input"
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowOld(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Mật khẩu mới (≥ 6 ký tự)</Label>
            <div className="relative">
              <Input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-[#0f172a] border-[#334155] text-slate-200 pr-9"
                data-testid="change-pw-new-input"
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setShowNew(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Xác nhận mật khẩu mới</Label>
            <Input
              type={showNew ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-[#0f172a] border-[#334155] text-slate-200"
              data-testid="change-pw-confirm-input"
              autoComplete="new-password"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-[10px] text-red-400 mt-1">Mật khẩu xác nhận không khớp</p>
            )}
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="border-[#334155] text-slate-300 hover:bg-[#334155]">Hủy</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !oldPassword || !newPassword || !confirmPassword}
            className="bg-amber-500 text-[#0f172a] font-semibold hover:bg-amber-400 gap-1"
            data-testid="change-pw-submit-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
            Lưu mật khẩu mới
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
